import "./styles.css";
import {
  ProductType,
  PRODUCT_TYPE_LABEL,
  MODALITY_LABEL,
  QuoteResult,
  computeQuote,
  computeTotalImages,
  formatMXN,
} from "./calculator";
import { AppState, Step, createInitialState, goToStep } from "./state";
import { isValidName, isValidPhone, isValidProductCount, isValidPhotosPerProduct, normalizePhone } from "./validation";
import { trackEvent, reportHeight } from "./analytics";
import { openWhatsApp } from "./whatsapp";
import {
  ModalityKey,
  PRODUCTS_MIN,
  PRODUCTS_MAX,
  PRODUCTS_DEFAULT,
  PHOTOS_PER_PRODUCT_MIN,
  PHOTOS_PER_PRODUCT_MAX,
  PHOTOS_PER_PRODUCT_DEFAULT,
} from "./pricing";

type Modality = ModalityKey;

const PRODUCT_TYPES: ProductType[] = [
  "footwear",
  "apparel",
  "cosmetics",
  "food_beverage",
  "jewelry_accessories",
  "industrial",
  "other",
];

const MODALITIES: Modality[] = ["whiteBackground", "flatLay", "invisibleMannequin", "ambient", "model", "onLocation"];

let state: AppState = createInitialState({
  productCount: PRODUCTS_DEFAULT,
  photosPerProduct: PHOTOS_PER_PRODUCT_DEFAULT,
});

let lastResult: QuoteResult | null = null;
let viewedResultOnce = false;

const app = document.getElementById("app")!;

if (window.self !== window.top) {
  document.documentElement.classList.add("producto-embedded");
}

function render() {
  app.innerHTML = "";
  app.appendChild(renderStepIndicator());
  if (state.step === 1) app.appendChild(renderStepScope());
  if (state.step === 2) app.appendChild(renderStepContact());
  if (state.step === 3) app.appendChild(renderStepResult());
  scheduleHeightReport();
}

// Keeps the embedding iframe sized to the real content height, so Webflow
// never has to guess a fixed height (avoids a nested/double scrollbar).
let heightReportRaf = 0;
function scheduleHeightReport() {
  if (heightReportRaf) cancelAnimationFrame(heightReportRaf);
  heightReportRaf = requestAnimationFrame(() => {
    reportHeight(app.getBoundingClientRect().height);
  });
}
if (typeof ResizeObserver !== "undefined") {
  new ResizeObserver(() => scheduleHeightReport()).observe(app);
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  attrs?: Record<string, string>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  }
  return node;
}

function renderStepIndicator(): HTMLElement {
  const wrap = el("div", "p-step-indicator");
  wrap.setAttribute("aria-hidden", "true");
  for (let i = 1; i <= 3; i++) {
    const dot = el("span", "p-step-dot", { "data-active": String(i === state.step) });
    wrap.appendChild(dot);
  }
  return wrap;
}

/* ---------- Paso 1 · Alcance ---------- */

function renderStepScope(): HTMLElement {
  const frag = el("div");

  const eyebrow = el("p", "p-eyebrow");
  eyebrow.textContent = "Paso 1 de 3";
  frag.appendChild(eyebrow);

  const title = el("h1", "p-title");
  title.textContent = "Cuéntanos qué vas a fotografiar";
  frag.appendChild(title);

  const subtitle = el("p", "p-subtitle");
  subtitle.textContent = "Esto nos da una referencia inicial. La propuesta final siempre se confirma contigo.";
  frag.appendChild(subtitle);

  // Tipo de producto
  const typeField = el("div", "p-field");
  const typeLabel = el("label", "p-label", { for: "productType" });
  typeLabel.textContent = "Tipo de producto";
  const typeSelect = el("select", "p-select", { id: "productType", name: "productType" });
  const placeholderOpt = el("option", undefined, { value: "" });
  placeholderOpt.textContent = "Selecciona una opción";
  typeSelect.appendChild(placeholderOpt);
  for (const pt of PRODUCT_TYPES) {
    const opt = el("option", undefined, { value: pt });
    opt.textContent = PRODUCT_TYPE_LABEL[pt];
    if (state.productType === pt) opt.setAttribute("selected", "selected");
    typeSelect.appendChild(opt);
  }
  typeSelect.addEventListener("change", () => {
    state.productType = (typeSelect.value || null) as ProductType | null;
    state.errors.productType = undefined;
  });
  typeField.appendChild(typeLabel);
  typeField.appendChild(typeSelect);
  if (state.errors.productType) {
    const err = el("p", "p-error");
    err.textContent = state.errors.productType;
    typeField.appendChild(err);
  }
  frag.appendChild(typeField);

  // Modalidad
  const modField = el("div", "p-field");
  const modLabel = el("span", "p-label");
  modLabel.textContent = "Modalidad";
  modField.appendChild(modLabel);
  const modList = el("div", "p-modalities", { role: "group", "aria-label": "Modalidad de fotografía" });
  for (const m of MODALITIES) {
    const btn = el("button", "p-modality", {
      type: "button",
      "aria-pressed": String(state.modality === m),
    });
    const name = el("span", "p-modality-name");
    name.textContent = MODALITY_LABEL[m];
    const note = el("span", "p-modality-note");
    note.textContent = modalityNote(m);
    btn.appendChild(name);
    btn.appendChild(note);
    btn.addEventListener("click", () => {
      state.modality = m;
      state.errors.modality = undefined;
      render();
    });
    modList.appendChild(btn);
  }
  modField.appendChild(modList);
  if (state.errors.modality) {
    const err = el("p", "p-error");
    err.textContent = state.errors.modality;
    modField.appendChild(err);
  }
  frag.appendChild(modField);

  // Productos / SKU y fotos por producto
  const grid = el("div", "p-grid-2");

  const countField = el("div", "p-field");
  const countLabel = el("label", "p-label", { for: "productCount" });
  countLabel.textContent = "Productos / SKU";
  const countHint = el("p", "p-hint");
  countHint.textContent = "¿Cuántos productos unitarios son?";
  const countInput = el("input", "p-input", {
    id: "productCount",
    name: "productCount",
    type: "number",
    inputmode: "numeric",
    min: String(PRODUCTS_MIN),
    max: String(PRODUCTS_MAX),
    value: String(state.productCount),
  });
  countInput.addEventListener("input", () => {
    state.productCount = Number(countInput.value);
    state.errors.productCount = undefined;
    updateTotal();
  });
  countField.appendChild(countLabel);
  countField.appendChild(countHint);
  countField.appendChild(countInput);
  if (state.errors.productCount) {
    const err = el("p", "p-error");
    err.textContent = state.errors.productCount;
    countField.appendChild(err);
  }
  grid.appendChild(countField);

  const photosField = el("div", "p-field");
  const photosLabel = el("label", "p-label", { for: "photosPerProduct" });
  photosLabel.textContent = "Fotos por producto";
  const photosHint = el("p", "p-hint");
  photosHint.textContent = "Frente, vuelta, detalles, etc.";
  const photosInput = el("input", "p-input", {
    id: "photosPerProduct",
    name: "photosPerProduct",
    type: "number",
    inputmode: "numeric",
    min: String(PHOTOS_PER_PRODUCT_MIN),
    max: String(PHOTOS_PER_PRODUCT_MAX),
    value: String(state.photosPerProduct),
  });
  photosInput.addEventListener("input", () => {
    state.photosPerProduct = Number(photosInput.value);
    state.errors.photosPerProduct = undefined;
    updateTotal();
  });
  photosField.appendChild(photosLabel);
  photosField.appendChild(photosHint);
  photosField.appendChild(photosInput);
  if (state.errors.photosPerProduct) {
    const err = el("p", "p-error");
    err.textContent = state.errors.photosPerProduct;
    photosField.appendChild(err);
  }
  grid.appendChild(photosField);

  frag.appendChild(grid);

  // Total de imágenes — deliberately styled apart from the CTA (see styles.css)
  const total = el("div", "p-total", { id: "totalBlock", "aria-live": "polite" });
  const totalLabel = el("span", "p-total-label");
  totalLabel.textContent = "Total de imágenes";
  const totalValue = el("span", "p-total-value", { id: "totalValue" });
  totalValue.textContent = String(computeTotalImages(state.productCount, state.photosPerProduct));
  total.appendChild(totalLabel);
  total.appendChild(totalValue);
  frag.appendChild(total);

  const continueBtn = el("button", "p-btn p-btn--primary", { type: "button" });
  continueBtn.textContent = "Continuar";
  continueBtn.addEventListener("click", () => {
    if (!validateScope()) {
      render();
      return;
    }
    trackEvent("product_calculator_scope", {
      productType: state.productType,
      modality: state.modality,
      totalImages: computeTotalImages(state.productCount, state.photosPerProduct),
    });
    state = goToStep(state, 2);
    render();
  });
  frag.appendChild(continueBtn);

  function updateTotal() {
    const v = document.getElementById("totalValue");
    if (v) v.textContent = String(computeTotalImages(state.productCount || 0, state.photosPerProduct || 0));
  }

  return frag;
}

function modalityNote(m: Modality): string {
  switch (m) {
    case "whiteBackground":
      return "Precio automático por volumen.";
    case "flatLay":
      return "Precio automático desde $200 por foto.";
    case "invisibleMannequin":
      return "Precio automático desde $600 por foto.";
    case "ambient":
      return "Desde $600 por foto — sujeto a validación de producción.";
    case "model":
      return "A la medida — cotización con equipo de producción.";
    case "onLocation":
      return "A la medida — cotización con equipo de producción.";
  }
}

function validateScope(): boolean {
  let ok = true;
  if (!state.productType) {
    state.errors.productType = "Selecciona un tipo de producto.";
    ok = false;
  }
  if (!state.modality) {
    state.errors.modality = "Selecciona una modalidad.";
    ok = false;
  }
  if (!isValidProductCount(state.productCount, PRODUCTS_MIN, PRODUCTS_MAX)) {
    state.errors.productCount = `Ingresa un número entre ${PRODUCTS_MIN} y ${PRODUCTS_MAX}.`;
    ok = false;
  }
  if (!isValidPhotosPerProduct(state.photosPerProduct, PHOTOS_PER_PRODUCT_MIN, PHOTOS_PER_PRODUCT_MAX)) {
    state.errors.photosPerProduct = `Ingresa un número entre ${PHOTOS_PER_PRODUCT_MIN} y ${PHOTOS_PER_PRODUCT_MAX}.`;
    ok = false;
  }
  return ok;
}

/* ---------- Paso 2 · Contacto ---------- */

function renderStepContact(): HTMLElement {
  const frag = el("div");

  const eyebrow = el("p", "p-eyebrow");
  eyebrow.textContent = "Paso 2 de 3";
  frag.appendChild(eyebrow);

  const title = el("h1", "p-title");
  title.textContent = "¿A dónde te enviamos la referencia?";
  frag.appendChild(title);

  const subtitle = el("p", "p-subtitle");
  subtitle.textContent = "Solo lo usamos para identificar tu solicitud en WhatsApp.";
  frag.appendChild(subtitle);

  const nameField = el("div", "p-field");
  const nameLabel = el("label", "p-label", { for: "name" });
  nameLabel.textContent = "Nombre";
  const nameInput = el("input", "p-input", {
    id: "name",
    name: "name",
    type: "text",
    autocomplete: "name",
    value: state.name,
  });
  nameInput.addEventListener("input", () => {
    state.name = nameInput.value;
    state.errors.name = undefined;
  });
  nameField.appendChild(nameLabel);
  nameField.appendChild(nameInput);
  if (state.errors.name) {
    const err = el("p", "p-error");
    err.textContent = state.errors.name;
    nameField.appendChild(err);
  }
  frag.appendChild(nameField);

  const phoneField = el("div", "p-field");
  const phoneLabel = el("label", "p-label", { for: "whatsapp" });
  phoneLabel.textContent = "WhatsApp";
  const phoneInput = el("input", "p-input", {
    id: "whatsapp",
    name: "whatsapp",
    type: "tel",
    inputmode: "tel",
    autocomplete: "tel",
    placeholder: "55 1234 5678",
    value: state.whatsapp,
  });
  phoneInput.addEventListener("input", () => {
    state.whatsapp = phoneInput.value;
    state.errors.whatsapp = undefined;
  });
  phoneField.appendChild(phoneLabel);
  phoneField.appendChild(phoneInput);
  if (state.errors.whatsapp) {
    const err = el("p", "p-error");
    err.textContent = state.errors.whatsapp;
    phoneField.appendChild(err);
  }
  frag.appendChild(phoneField);

  const actions = el("div", "p-actions");

  const continueBtn = el("button", "p-btn p-btn--primary", { type: "button" });
  continueBtn.textContent = "Ver referencia";
  continueBtn.addEventListener("click", () => {
    if (!validateContact()) {
      render();
      return;
    }
    state.whatsapp = normalizePhone(state.whatsapp);
    lastResult = computeQuote({
      productType: state.productType!,
      modality: state.modality!,
      productCount: state.productCount,
      photosPerProduct: state.photosPerProduct,
    });
    trackEvent("product_calculator_result", {
      modality: state.modality,
      kind: lastResult.kind,
      totalImages: lastResult.totalImages,
    });
    viewedResultOnce = false;
    state = goToStep(state, 3);
    render();
  });
  actions.appendChild(continueBtn);

  const backBtn = el("button", "p-link", { type: "button" });
  backBtn.textContent = "← Corregir alcance";
  backBtn.addEventListener("click", () => {
    state = goToStep(state, 1);
    render();
  });
  actions.appendChild(backBtn);

  frag.appendChild(actions);
  return frag;
}

function validateContact(): boolean {
  let ok = true;
  if (!isValidName(state.name)) {
    state.errors.name = "Ingresa tu nombre.";
    ok = false;
  }
  if (!isValidPhone(state.whatsapp)) {
    state.errors.whatsapp = "Ingresa un WhatsApp válido.";
    ok = false;
  }
  return ok;
}

/* ---------- Paso 3 · Resultado ---------- */

function renderStepResult(): HTMLElement {
  const frag = el("div");

  if (!lastResult) {
    // Defensive: should never happen via normal flow.
    state = goToStep(state, 1);
    return renderStepScope();
  }
  const result = lastResult;

  if (!viewedResultOnce) {
    trackEvent("product_calculator_view", {});
    viewedResultOnce = true;
  }

  const eyebrow = el("p", "p-eyebrow");
  eyebrow.textContent = "Paso 3 de 3";
  frag.appendChild(eyebrow);

  const title = el("h1", "p-title");
  title.textContent = "Tu referencia inicial";
  frag.appendChild(title);

  const card = el("div", "p-result-card", { "aria-live": "polite" });

  const cardEyebrow = el("p", "p-result-eyebrow");
  cardEyebrow.textContent = result.kind === "automatic" ? "Referencia inicial" : "Cotización a la medida";
  card.appendChild(cardEyebrow);

  const amount = el("p", "p-result-amount");
  amount.textContent = result.kind === "automatic" ? formatMXN(result.amount) : "A cotizar";
  card.appendChild(amount);

  const detail = el("p", "p-result-detail");
  detail.textContent =
    result.kind === "automatic"
      ? `${result.totalImages} imágenes · ${MODALITY_LABEL[state.modality!]}`
      : `${result.totalImages} imágenes · ${MODALITY_LABEL[state.modality!]} · requiere revisión de nuestro equipo de producción`;
  card.appendChild(detail);

  if (result.kind === "automatic" && result.requiresProductionValidation) {
    const badge = el("span", "p-badge");
    badge.textContent = "Sujeto a validación de producción";
    card.appendChild(badge);
  }

  const summary = el("dl", "p-summary");
  const rows: [string, string][] = [
    ["Tipo de producto", state.productType ? PRODUCT_TYPE_LABEL[state.productType] : "—"],
    ["Productos / SKU", String(state.productCount)],
    ["Fotos por producto", String(state.photosPerProduct)],
    ["Total de imágenes", String(result.totalImages)],
  ];
  for (const [label, value] of rows) {
    const row = el("div", "p-summary-row");
    const dt = el("dt");
    dt.textContent = label;
    const dd = el("dd");
    dd.textContent = value;
    row.appendChild(dt);
    row.appendChild(dd);
    summary.appendChild(row);
  }
  card.appendChild(summary);

  const note = el("p", "p-note");
  note.textContent =
    "Esta referencia no constituye cotización formal, contrato ni reserva. La propuesta final se confirma después de revisar producto, preparación, logística y requisitos.";
  card.appendChild(note);

  frag.appendChild(card);

  const actions = el("div", "p-actions");

  const waBtn = el("button", "p-btn p-btn--whatsapp", { type: "button" });
  waBtn.textContent = "Confirmar cotización por WhatsApp";
  waBtn.addEventListener("click", () => {
    trackEvent("whatsapp_quote_ready", { kind: result.kind });
    openWhatsApp(state, result);
  });
  actions.appendChild(waBtn);

  const backBtn = el("button", "p-link", { type: "button" });
  backBtn.textContent = "← Corregir datos";
  backBtn.addEventListener("click", () => {
    state = goToStep(state, 2);
    render();
  });
  actions.appendChild(backBtn);

  frag.appendChild(actions);

  return frag;
}

// Fire the initial view event only once the app first mounts on step 1,
// separate from the step-3 "view result" event above.
trackEvent("product_calculator_view", { step: 1 });
render();
